import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, Building2, Camera, BarChart3, MessageSquare, Clock, Star, TrendingUp, Lock, Sparkles } from 'lucide-react';
import { sampleIndustryComparison, samplePricingPackages } from '../lib/samplePreviewData';
import { getSamplePreviewEnabled, subscribeToSamplePreviewMode } from '../lib/samplePreviewMode';
import {
  CLIENT_PREMIUM_REDIRECT_PATH,
  OWNER_PRICING_LANE_TEST_ID,
} from '../lib/pricingLaneDisplay';
import PricingPlansGrid from '../components/pricing/PricingPlansGrid';
import { arePaymentsEnabled } from '../lib/paymentsAvailability';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const paymentsEnabled = arePaymentsEnabled();

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { shouldUseClientPremiumPricingLane: useClientPremiumLane } = useRoleContext();
  const { t, locale } = useLanguage();
  const [packages, setPackages] = useState({});
  const [comparison, setComparison] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentTier, setCurrentTier] = useState('RECON');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [samplePreviewEnabled, setSamplePreviewState] = useState(getSamplePreviewEnabled());

  useEffect(() => subscribeToSamplePreviewMode(setSamplePreviewState), []);

  useEffect(() => {
    fetchPackages();
    if (user && !samplePreviewEnabled) fetchCurrentSubscription();
  }, [user, samplePreviewEnabled]);

  const applySamplePreview = () => {
    setPackages(samplePricingPackages);
    setComparison(sampleIndustryComparison);
    setCurrentTier('RECON');
    setLoading(false);
  };

  const fetchPackages = async () => {
    if (samplePreviewEnabled) {
      applySamplePreview();
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetch(`${API_URL}/api/packages`);
      const data = await response.json();
      if (!data.packages || Object.keys(data.packages).length === 0) {
        // Batch 6: never show fabricated pricing/savings to real visitors — surface
        // an honest error instead of sample tiers (sample data is preview-only).
        setPackages({});
        setComparison({});
        setLoadError(true);
        return;
      }
      setPackages(data.packages || {});
      setComparison(data.industry_comparison || {});
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      setPackages({});
      setComparison({});
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/user/subscription`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setCurrentTier(data.tier);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const handleSelectPlan = async (tier) => {
    if (samplePreviewEnabled) {
      navigate('/register?plan=' + tier.toLowerCase());
      return;
    }
    if (!user) {
      navigate('/register?plan=' + tier.toLowerCase());
      return;
    }

    const pkg = packages[tier];
    if (pkg && pkg.price_monthly > 0 && !paymentsEnabled) {
      navigate('/contact');
      return;
    }

    if (pkg && pkg.price_monthly === 0) {
      setCurrentTier(tier);
      navigate('/register?plan=' + tier.toLowerCase());
      return;
    }

    if (!paymentsEnabled) {
      navigate('/contact');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tier, billing_cycle: billingCycle, origin_url: window.location.origin }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          setCurrentTier(tier);
        }
      }
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  };

  const getPlanButtonLabel = (rawPackage, isCurrentPlan) => {
    if (isCurrentPlan) return t('pricing.btn_current_plan', 'Current Plan');
    if (rawPackage.price_monthly === 0) return t('pricing.btn_get_started', 'Get Started');
    if (!paymentsEnabled) return t('payments.contact_us', 'Contact us');
    return t('pricing.btn_upgrade', 'Upgrade');
  };

  const featureLabels = useMemo(() => ({
    max_listings: { label: t('pricing.feature.max_listings', 'Property Listings'), icon: Building2 },
    photos_per_listing: { label: t('pricing.feature.photos_per_listing', 'Photos per Listing'), icon: Camera },
    virtual_tour: { label: t('pricing.feature.virtual_tour', 'Virtual Tours'), icon: Star },
    weekly_reports: { label: t('pricing.feature.weekly_reports', 'Weekly Reports'), icon: BarChart3 },
    priority_placement: { label: t('pricing.feature.priority_placement', 'Priority Placement'), icon: TrendingUp },
    concierge_support: { label: t('pricing.feature.concierge_support', 'Concierge Support'), icon: MessageSquare },
    response_time_guarantee: { label: t('pricing.feature.response_time_guarantee', 'Response Guarantee'), icon: Clock },
    custom_branding: { label: t('pricing.feature.custom_branding', 'Custom Branding'), icon: Sparkles },
    api_access: { label: t('pricing.feature.api_access', 'API Access'), icon: Lock },
  }), [t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-signal-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user && useClientPremiumLane) {
    return <Navigate to={CLIENT_PREMIUM_REDIRECT_PATH} replace />;
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="pricing-page" data-pricing-lane={OWNER_PRICING_LANE_TEST_ID}>
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <Badge className="bg-military-gold/20 text-military-gold border-military-gold/30 mb-4">{t('pricing.badge', 'Clear Pricing')}</Badge>
        <Badge className="bg-signal-blue/10 text-signal-blue border-signal-blue/20 mb-4 ml-2">{t('pricing.lane_owner_badge', 'For Property Owners')}</Badge>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-800 mb-4">{t('pricing.title', 'CHOOSE YOUR PLAN')}</h1>
        <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-4">{t('pricing.subtitle', 'Standard listings have no commissions. TLA booking success fees, when applicable, are clearly disclosed before confirmation.')}</p>
        <p className="text-slate-500 text-sm max-w-2xl mx-auto mb-8">{t('pricing.tla_fee_disclosure', 'TLA bookings may include a clearly disclosed success fee when a booking or contract is confirmed.')}</p>

        {samplePreviewEnabled && (
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-signal-blue/10 border border-signal-blue/20 rounded-full text-sm text-signal-blue">
            {t('pricing.sample_preview_banner', 'Sample preview mode is active. Pricing content below is demo-ready sample content for client walkthroughs.')}
          </div>
        )}

        {!paymentsEnabled && !samplePreviewEnabled && (
          <div
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 bg-amber-50 border border-amber-200 rounded-full text-sm text-amber-900"
            data-testid="payments-unavailable-notice"
          >
            {t('payments.unavailable_notice', 'Online checkout is not available yet. Contact us for paid plan upgrades.')}
          </div>
        )}

        <div className="inline-flex items-center gap-2 bg-slate-800 rounded-full p-1 mb-12">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-signal-blue text-white' : 'text-slate-600 hover:text-slate-200'}`}
          >
            {t('pricing.billing_monthly', 'Monthly')}
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'yearly' ? 'bg-signal-blue text-white' : 'text-slate-600 hover:text-slate-200'}`}
          >
            {t('pricing.billing_yearly', 'Yearly')}
            <span className="ml-1 text-emerald-400 text-xs">{t('pricing.billing_yearly_savings', 'Save 17%')}</span>
          </button>
        </div>
      </div>

      {loadError ? (
        <div className="max-w-2xl mx-auto my-12 bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center">
          <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">{t('pricing.load_error_title', 'We couldn’t load pricing just now')}</h2>
          <p className="text-slate-600 mb-6">{t('pricing.load_error_body', 'Something went wrong reaching our servers. Please try again in a moment.')}</p>
          <Button onClick={fetchPackages} className="bg-signal-blue hover:bg-signal-blue-hover text-white">{t('pricing.retry', 'Retry')}</Button>
        </div>
      ) : (
        <PricingPlansGrid
          packages={packages}
          locale={locale}
          billingCycle={billingCycle}
          currentPlanId={user ? currentTier : null}
          featureLabels={featureLabels}
          getPlanButtonLabel={getPlanButtonLabel}
          onSelectPlan={handleSelectPlan}
          paymentsEnabled={paymentsEnabled}
          t={t}
        />
      )}

      {comparison.trust_factors?.length > 0 && (
      <div className="bg-white/50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-heading text-3xl font-bold text-slate-800 text-center mb-4">{t('pricing.trust_title', 'WHY MILITARY FAMILIES TRUST US')}</h2>
          <p className="text-slate-600 text-center max-w-2xl mx-auto mb-12">{t('pricing.trust_subtitle', "We're not just another listing site. We're built by people who understand PCS moves.")}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {comparison.trust_factors?.slice(0, 6).map((factor, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 bg-military-gold/20 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-military-gold" />
                </div>
                <h3 className="font-bold text-slate-800 mb-2">{factor.title}</h3>
                <p className="text-sm text-slate-600">{factor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="font-heading text-3xl font-bold text-slate-800 text-center mb-12">{t('pricing.compare_title', 'HOW WE COMPARE')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4">{t('pricing.compare_airbnb_title', 'vs. Airbnb & Booking.com')}</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-200"><span className="text-slate-600">{t('pricing.their_fees', 'Their fees')}</span><span className="text-red-400">{comparison.vs_airbnb?.their_fee || t('pricing.fees_high_variable', 'High variable fees')}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-slate-200"><span className="text-slate-600">{t('pricing.our_fees', 'Our fees')}</span><span className="text-emerald-400">{comparison.vs_airbnb?.our_fee || t('pricing.fees_flat_subscription', 'Flat subscription')}</span></div>
              <div className="flex justify-between items-center py-2"><span className="text-slate-600">{t('pricing.annual_savings', 'Annual savings')}</span><span className="text-military-gold font-bold">{comparison.vs_airbnb?.annual_savings || t('pricing.savings_owner', 'Meaningful owner savings')}</span></div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4">{t('pricing.compare_agencies_title', 'vs. Traditional Agencies')}</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-slate-200"><span className="text-slate-600">{t('pricing.their_fees', 'Their fees')}</span><span className="text-red-400">{comparison.vs_traditional?.their_fee || t('pricing.fees_high_commissions', 'High commissions')}</span></div>
              <div className="flex justify-between items-center py-2 border-b border-slate-200"><span className="text-slate-600">{t('pricing.our_fees', 'Our fees')}</span><span className="text-emerald-400">{comparison.vs_traditional?.our_fee || t('pricing.fees_flat_subscription', 'Flat subscription')}</span></div>
              <div className="flex justify-between items-center py-2"><span className="text-slate-600">{t('pricing.annual_savings', 'Annual savings')}</span><span className="text-military-gold font-bold">{comparison.vs_traditional?.annual_savings || t('pricing.savings_margin', 'Stronger operating margin')}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-signal-blue/20 to-military-gold/20 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">{t('pricing.cta_title', 'READY TO GET STARTED?')}</h2>
          <p className="text-slate-600 mb-8">{t('pricing.cta_subtitle', "Join military families and serious owners who want a clearer relocation decision engine, not another generic marketplace.")}</p>
          <Button onClick={() => navigate('/register')} size="lg" className="bg-military-gold hover:bg-military-gold/90 text-slate-950 px-8">{t('pricing.cta_btn', 'Start Free Today')}</Button>
        </div>
      </div>
    </div>
  );
}
